<!-- Error -->
@if (healthError && !isLoadingHealth) {
  <div class="error-banner">
    {{ healthError }}
  </div>
}

<!-- Content -->
<div>
  <div class="health-actions">
    @if (lastRefreshed) {
      <span class="last-refreshed"
        >Last refreshed:
        {{ lastRefreshed | date: 'MM/dd/yyyy h:mm:ss a' }}</span
      >
    }
    <ukg-button
      emphasis="medium"
      size="medium"
      (click)="loadSystemHealth()"
      (keydown.enter)="loadSystemHealth()"
      >Refresh</ukg-button
    >
  </div>

  <!-- Stuck Requests -->
  <app-diagnostic-stuck-requests
    [stuckSummary]="systemHealth.stuckRequests"
    [currentPage]="stuckPage"
    [currentPageSize]="stuckPageSize"
    (pageChange)="onStuckPageChange($event)"
    (pageSizeChange)="onStuckPageSizeChange($event)"
    (fixVisibilityForRequest)="onFixVisibilityForRequest($event)"
    (releaseProcessingLockForRequest)="
      onReleaseProcessingLockForRequest($event)
    "
    (markFileReadyForStuck)="onMarkFileReadyForStuck($event)"
    (fixAllVisibility)="onFixVisibilityBulk()"
    (refresh)="getStuckRequests(stuckPage, stuckPageSize)"
    [loading]="stuckRequestsConfig.loading"
    [showSection]="stuckRequestsConfig.showSection">
  </app-diagnostic-stuck-requests>

  <!-- Rejected Files -->
  @if (rejectedFilesConfig.showSection) {
    <div class="result-card result-card-spacing">
      <h3>
        Rejected Files (last 24 hours)
        <span
          class="count-badge"
          [class.count-bad]="
            (systemHealth.rejectedFiles
              ? systemHealth.rejectedFiles.length
              : 0) > 0
          "
          >{{
            !!systemHealth.rejectedFiles ? systemHealth.rejectedFiles.length : 0
          }}</span
        >
        <button
          class="card-refresh-btn"
          (click)="getRejectedFiles()"
          title="Refresh">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            stroke-linejoin="round">
            <polyline points="23 4 23 10 17 10"></polyline>
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
          </svg>
        </button>
      </h3>
      @if (rejectedFilesConfig.loading) {
        <div class="spinner">
          <ukg-progress-circular
            size="small"
            indeterminate="true"
            description="Loading Rejected Files..." />
        </div>
      } @else {
        @if (rejectedFiles && rejectedFiles.rejectedFiles.length === 0) {
          <p class="health-ok">✓ No rejected files</p>
        }
        @if (rejectedFiles && rejectedFiles.rejectedFiles.length > 0) {
          <div class="table-wrapper">
            <table class="data-table compact">
              <thead>
                <tr>
                  <th>File Name</th>
                  <th>AR Number</th>
                  <th>Request ID</th>
                  <th>Source</th>
                  <th>Rejection Reason</th>
                  <th>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                @for (r of rejectedFiles.rejectedFiles; track r) {
                  <tr class="row-mismatch">
                    <td class="file-name">{{ r.fileName ?? '—' }}</td>
                    <td>{{ r.arNumber ?? '—' }}</td>
                    <td>{{ r.requestId ?? '—' }}</td>
                    <td>
                      <span class="source-badge">{{ r.source }}</span>
                    </td>
                    <td class="rejection-reason">
                      {{ r.rejectionReason ?? '—' }}
                    </td>
                    <td>{{ r.timestamp | date: 'MM/dd h:mm a' }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      }
    </div>
  }

  <!-- Health Grid -->
  @if (windowsHealthCheckConfig.showSection) {
    <div class="health-grid">
      <div class="result-card">
        <h3>
          Windows Services
          <button
            class="card-refresh-btn"
            (click)="getWindowsServiceHeartbeats()"
            title="Refresh">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round">
              <polyline points="23 4 23 10 17 10"></polyline>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
            </svg>
          </button>
        </h3>
        @if (windowsHealthCheckConfig.loading) {
          <div class="spinner">
            <ukg-progress-circular
              size="small"
              indeterminate="true"
              description="Loading Windows Health..." />
          </div>
        } @else {
          @if (windowsHealth && windowsHealth.serviceHeartbeats.length === 0) {
            <p class="health-ok">
              No heartbeat data (run migration to enable).
            </p>
          }
          @if (windowsHealth && windowsHealth.serviceHeartbeats.length > 0) {
            <div class="infra-list">
              @for (h of windowsHealth.serviceHeartbeats; track h) {
                <div class="infra-row">
                  <span class="infra-name">{{ h.serviceName }}</span>
                  <span class="infra-desc"
                    >Last heartbeat:
                    {{
                      h.lastHeartbeat ? (h.lastHeartbeat | date: 'h:mm a') : '—'
                    }}</span
                  >
                  @if (h.lastHeartbeat && !h.isStale) {
                    <span class="health-status-badge hs-ok">Running</span>
                  }
                  @if (h.lastHeartbeat && h.isStale) {
                    <span class="stale-label"
                      >Stale ({{ formatAge(h.minutesSinceHeartbeat) }})</span
                    >
                  }
                  @if (!h.lastHeartbeat) {
                    <span class="health-status-badge hs-unknown">Unknown</span>
                  }
                </div>
              }
            </div>
          }
        }
      </div>
      <!-- Database Connectivity -->
      @if (databaseConnectivityConfig.showSection) {
        <div class="result-card">
          <h3>
            Database Connectivity
            <button
              class="card-refresh-btn"
              (click)="refresh(InfraCategory.Database)"
              title="Refresh">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
              </svg>
            </button>
          </h3>
          @if (databaseConnectivityConfig.loading) {
            <div class="spinner">
              <ukg-progress-circular
                size="small"
                indeterminate="true"
                description="Loading Windows Health..." />
            </div>
          } @else {
            <div class="infra-list">
              @for (c of infraChecks('Database'); track c) {
                <div
                  class="infra-row"
                  [class.infra-row-error]="c.status !== 'Connected'">
                  <span class="infra-name">{{ c.name }}</span>
                  <span class="infra-desc">Response: {{ c.responseMs }}ms</span>
                  <span
                    class="health-status-badge"
                    [class]="infraStatusClass(c.status)"
                    >{{ c.status }}</span
                  >
                </div>
              }
            </div>
          }
        </div>
      }
      <!-- Object Storage -->
      @if (objectStorageHealthConfig.showSection) {
        <div class="result-card">
          <h3>
            Object Storage (Swift)
            <button
              class="card-refresh-btn"
              (click)="refresh(InfraCategory.ObjectStorage)"
              title="Refresh">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
              </svg>
            </button>
          </h3>
          @if (objectStorageHealthConfig.loading) {
            <div class="spinner">
              <ukg-progress-circular
                size="small"
                indeterminate="true"
                description="Loading Objecct Storage Health..." />
            </div>
          } @else {
            <div class="infra-list">
              @for (c of infraChecks('ObjectStorage'); track c) {
                <div
                  class="infra-row"
                  [class.infra-row-error]="c.status !== 'Authenticated'">
                  <span class="infra-name">{{ c.name }}</span>
                  <span class="infra-desc">{{ c.description }}</span>
                  <span
                    class="health-status-badge"
                    [class]="infraStatusClass(c.status)"
                    >{{ c.status }}</span
                  >
                </div>
              }
            </div>
          }
        </div>
      }
      <!-- Vendor SFTP -->
      @if (sftpHealthConfig.showSection) {
        <div class="result-card">
          <h3>
            Vendor SFTP
            <button
              class="card-refresh-btn"
              (click)="refresh(InfraCategory.Sftp)"
              title="Refresh">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
              </svg>
            </button>
          </h3>
          @if (sftpHealthConfig.loading) {
            <div class="spinner">
              <ukg-progress-circular
                size="small"
                indeterminate="true"
                description="Loading Sftp Health..." />
            </div>
          } @else {
            @if (infraChecks('Sftp').length === 0) {
              <p class="empty-state">No SFTP hosts configured.</p>
            }
            @if (infraChecks('Sftp').length > 0) {
              <div class="infra-list">
                @for (c of infraChecks('Sftp'); track c) {
                  <div
                    class="infra-row"
                    [class.infra-row-error]="c.status !== 'Connected'">
                    <span class="infra-name">{{ c.name }}</span>
                    <span class="infra-desc">{{ c.description }}</span>
                    <span
                      class="health-status-badge"
                      [class]="infraStatusClass(c.status)"
                      >{{ c.status }}</span
                    >
                  </div>
                }
              </div>
            }
          }
        </div>
      }
      <!-- Authentication Endpoints -->
      @if (authEndpointConfig.showSection) {
        <div class="result-card">
          <h3>
            Authentication Endpoints
            <button
              class="card-refresh-btn"
              (click)="refresh(InfraCategory.External)"
              title="Refresh">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
              </svg>
            </button>
          </h3>
          @if (authEndpointConfig.loading) {
            <div class="spinner">
              <ukg-progress-circular
                size="small"
                indeterminate="true"
                description="Loading Sftp Health..." />
            </div>
          } @else {
            <div class="infra-list">
              @for (c of infraChecks('External'); track c) {
                <div
                  class="infra-row"
                  [class.infra-row-error]="c.status !== 'Reachable'">
                  <span class="infra-name">{{ c.name }}</span>
                  <span class="infra-desc">{{ c.description }}</span>
                  <span
                    class="health-status-badge"
                    [class]="infraStatusClass(c.status)"
                    >{{ c.status }}</span
                  >
                </div>
              }
            </div>
          }
        </div>
      }
      <!-- Email (SMTP) -->
      @if (smtpConfig.showSection) {
        <div class="result-card">
          <h3>
            Email (SMTP)
            <button
              class="card-refresh-btn"
              (click)="refresh(InfraCategory.Email)"
              title="Refresh">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
              </svg>
            </button>
          </h3>
          @if (smtpConfig.loading) {
            <div class="spinner">
              <ukg-progress-circular
                size="small"
                indeterminate="true"
                description="Loading Sftp Health..." />
            </div>
          } @else {
            <div class="infra-list">
              @for (c of infraChecks('Email'); track c) {
                <div
                  class="infra-row"
                  [class.infra-row-error]="c.status !== 'Connected'">
                  <span class="infra-name">{{ c.name }}</span>
                  <span class="infra-desc">{{ c.description }}</span>
                  <span
                    class="health-status-badge"
                    [class]="infraStatusClass(c.status)"
                    >{{ c.status }}</span
                  >
                </div>
              }
            </div>
          }
        </div>
      }
    </div>
  }
</div>

<app-confirm-dialog />
